import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Search, MoreHorizontal, Globe, Check, X, ExternalLink, Trash2, Edit } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'
import type { Domain } from '@/types'

function AddDomainDialog() {
  const [open, setOpen] = useState(false)
  const [domainName, setDomainName] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const addDomainMutation = useMutation({
    mutationFn: (name: string) => apiClient.post<Domain>('/domains', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast({ title: 'Domain added', description: 'Domain has been added successfully' })
      setOpen(false)
      setDomainName('')
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add domain', variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button id="add-domain-trigger">
          <Plus className="mr-2 h-4 w-4" />
          Add Domain
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Domain</DialogTitle>
          <DialogDescription>
            Enter the domain name you want to track. You&apos;ll need to verify ownership after adding.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="domain">Domain Name</Label>
            <Input
              id="domain"
              placeholder="example.com"
              value={domainName}
              onChange={(e) => setDomainName(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => addDomainMutation.mutate(domainName)}
            disabled={!domainName || addDomainMutation.isPending}
          >
            {addDomainMutation.isPending ? 'Adding...' : 'Add Domain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DomainCard({ domain }: { domain: Domain }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/domains/${domain.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast({ title: 'Domain deleted', description: 'Domain has been removed' })
    },
  })

  const daysUntilExpiry = domain.expiryDate
    ? Math.ceil((new Date(domain.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base font-medium">{domain.name}</CardTitle>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/domains/${domain.id}`}>
                <Edit className="mr-2 h-4 w-4" />
                View Details
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`https://${domain.name}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Website
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => deleteMutation.mutate()}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant={domain.verified ? 'success' : 'secondary'}>
            {domain.verified ? (
              <>
                <Check className="mr-1 h-3 w-3" /> Verified
              </>
            ) : (
              <>
                <X className="mr-1 h-3 w-3" /> Unverified
              </>
            )}
          </Badge>
          {daysUntilExpiry !== null && daysUntilExpiry <= 30 && (
            <Badge variant={daysUntilExpiry <= 7 ? 'destructive' : 'warning'}>
              Expires in {daysUntilExpiry} days
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground space-y-1">
          {domain.registrar && <p>Registrar: {domain.registrar}</p>}
          {domain.expiryDate && (
            <p>Expires: {new Date(domain.expiryDate).toLocaleDateString()}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function DomainsPage() {
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => apiClient.get<Domain[]>('/domains'),
  })

  const domains = data?.data || []
  const filteredDomains = domains.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Domains</h1>
          <p className="text-muted-foreground">
            Manage and monitor your domains
          </p>
        </div>
        <AddDomainDialog />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search domains..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredDomains.length === 0 ? (
        <EmptyState
          icon={Globe}
          title={search ? 'No domains match your search' : 'No domains found'}
          description={
            search
              ? "Try adjusting your search terms."
              : "You haven't added any domains yet. add one to get started."
          }
          action={
            !search
              ? {
                  label: 'Add Domain',
                  onClick: () => document.getElementById('add-domain-trigger')?.click(),
                }
              : undefined
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDomains.map((domain) => (
            <DomainCard key={domain.id} domain={domain} />
          ))}
        </div>
      )}
    </div>
  )
}
